import { useCallback, useEffect, useState } from 'react';
import { openDrawer } from 'Services/Drawer';
import NotificationService from 'App/babel/NotificationService';
import logError from 'Helpers/logError';
import { unwrap, type ActionableStep, type StepEmailDraft } from './types';

type Args = {
	// Exactly one: the account card loads a company's reaches, the contact card one
	// person's.
	clientId?: number;
	contactId?: number;
	// Fires once a step's reach is recorded, so the host can move its own progress
	// without refetching (the widget shows both in one place — WEB-8739).
	onStepDone?: (step: ActionableStep) => void;
};

/**
 * The outreach a rep has approved but not yet carried out, plus the two things you can
 * do with a step: send its email, or record that the reach happened.
 *
 * Lives in a hook because the account card and the contact card render the same rows
 * from different keys, and both need the same "doing it updates the progress" wiring.
 */
export const useOutreachSteps = ({ clientId, contactId, onStepDone }: Args) => {
	const [steps, setSteps] = useState<ActionableStep[]>([]);
	const [busyStepId, setBusyStepId] = useState<number | null>(null);

	const url = contactId
		? `/api/v2/prospectingAgentRuns/contact/${contactId}/actionableSteps`
		: `/api/v2/prospectingAgentRuns/client/${clientId}/actionableSteps`;
	const id = contactId ?? clientId;

	useEffect(() => {
		// The Angular react-driver can mount this before the account/contact has loaded,
		// so the id is briefly undefined — stay empty until it arrives (the driver's
		// `watch` re-renders us once it resolves).
		if (!id || id <= 0) {
			setSteps([]);
			return;
		}
		let cancelled = false;
		fetch(url)
			.then(r => r.json())
			.then(body => {
				if (cancelled) return;
				const rows = unwrap<ActionableStep[]>(body);
				setSteps(Array.isArray(rows) ? rows : []);
			})
			.catch(err => {
				// Don't blank the card on a transient failure — leave the section empty
				// and surface the cause for diagnosis.
				logError(err, 'useOutreachSteps: failed to load actionable steps');
			});
		return () => {
			cancelled = true;
		};
	}, [url, id]);

	// The reach happened (or the rep is clearing it): complete the step and close its
	// to-do server-side, then drop the row so the list only shows work that's left.
	const recordDone = useCallback(
		async (step: ActionableStep) => {
			setBusyStepId(step.stepId);
			try {
				const res = await fetch(`/api/v2/prospectingAgentRuns/engagementStep/${step.stepId}/outreachDone`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' }
				});
				if (!res.ok) throw new Error(`outreachDone responded ${res.status}`);
				setSteps(prev => prev.filter(s => s.stepId !== step.stepId));
				onStepDone?.(step);
			} catch (err) {
				logError(err, 'useOutreachSteps: failed to record the outreach');
				NotificationService.add({
					icon: 'times',
					style: NotificationService.style.ERROR,
					title: 'prospectingAgent.outreachDoneFailed'
				});
			} finally {
				setBusyStepId(null);
			}
		},
		[onStepDone]
	);

	// Open the composer on the step's draft. The body arrives already composed (signed
	// server-side), and the drawer only inserts the default signature into an *empty*
	// body, so a prefilled draft keeps exactly what the server sent.
	const sendEmail = useCallback(
		async (step: ActionableStep) => {
			setBusyStepId(step.stepId);
			try {
				const res = await fetch(`/api/v2/prospectingAgentRuns/engagementStep/${step.stepId}/emailDraft`);
				if (!res.ok) throw new Error(`emailDraft responded ${res.status}`);
				const draft = unwrap<StepEmailDraft>(await res.json());
				if (!draft?.contact?.email) throw new Error('emailDraft returned no recipient');
				openDrawer('NewSingleMail', {
					recipients: {
						to: [
							{
								type: 'contact',
								id: draft.contact.id,
								name: draft.contact.name ?? '',
								email: draft.contact.email,
								title: draft.contact.title ?? undefined,
								// The company rides on the recipient; the drawer derives its own
								// client from it (getClientFromTo), so it needs no second lookup.
								client: { id: draft.client.id, name: draft.client.name ?? '' }
							}
						],
						cc: [],
						bcc: []
					},
					subject: draft.subject,
					body: draft.body,
					// The mail left, so the step's reach is done.
					onSend: () => recordDone(step)
				});
			} catch (err) {
				logError(err, 'useOutreachSteps: failed to open the email draft');
				NotificationService.add({
					icon: 'times',
					style: NotificationService.style.ERROR,
					title: 'prospectingAgent.emailDraftFailed'
				});
			} finally {
				setBusyStepId(null);
			}
		},
		[recordDone]
	);

	return { steps, busyStepId, recordDone, sendEmail };
};
